import React, { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useIsMobile } from '../hooks/useMediaQuery';
import { getAppData, saveAppData } from '../lib/storage';
import type { AppData, Workspace } from '@ext-bookmark/shared/types';
import WorkspaceSidebar from '../components/WorkspaceSidebar';
import CustomLayout from '../components/CustomLayout';
import SearchBar from '../components/SearchBar';
import Settings from '../components/Settings';
import AuthModal from '../components/AuthModal';
import { normalizeUrl } from '@ext-bookmark/shared/utils';

const HomePage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();
  const { config: themeConfig, setMode } = useTheme();
  const isMobile = useIsMobile();
  const [sidebarOverlayOpen, setSidebarOverlayOpen] = useState(false);
  const sidebarTheme = themeConfig?.sidebar || { width: 120, opacity: 1, backgroundColor: '#1F2937', textColor: '#F9FAFB', activeColor: '#2563EB', hoverColor: '#374151', borderColor: '#374151' };
  const sidebarWidth = isMobile ? 0 : sidebarTheme.width;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false); // 超时后强制显示，避免一直加载

  // 使用 React Query 加载应用数据
  const { data: appData, isLoading: isLoadingAppData } = useQuery({
    queryKey: ['app', 'data'],
    queryFn: async (): Promise<AppData> => {
      return await getAppData();
    },
    staleTime: 30 * 1000, // 30秒内不重新获取
    gcTime: 5 * 60 * 1000,
    retry: 1,
    retryDelay: 1000,
  });

  const [currentWorkspace, setCurrentWorkspaceState] = useState<Workspace | null>(null);
  const [searchMode, setSearchMode] = useState<'workspace' | 'all'>('workspace');

  // 设置当前工作区
  useEffect(() => {
    if (appData) {
      if (appData.currentWorkspaceId) {
        const workspace = appData.workspaces.find(w => w.id === appData.currentWorkspaceId);
        setCurrentWorkspaceState(workspace || null);
      } else {
        setCurrentWorkspaceState(appData.workspaces[0] || null);
      }
    }
  }, [appData]);

  // 添加超时保护：如果加载超过 3 秒，强制显示页面（网络慢、Supabase 不可达或未配置时）
  useEffect(() => {
    if (authLoading || isLoadingAppData) {
      setLoadingTimeout(false);
      timeoutRef.current = setTimeout(() => {
        setLoadingTimeout(true);
      }, 3000);
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setLoadingTimeout(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [authLoading, isLoadingAppData]);

  const handleWorkspaceChange = async (workspace: Workspace | null) => {
    setCurrentWorkspaceState(workspace);
    if (workspace && appData) {
      const updatedData = { ...appData };
      updatedData.currentWorkspaceId = workspace.id;
      await saveAppData(updatedData);
      // 使 React Query 缓存失效，触发重新获取
      queryClient.invalidateQueries({ queryKey: ['app', 'data'] });
    }
  };

  const handleDataRefresh = async () => {
    // 使 React Query 缓存失效，触发重新获取
    queryClient.invalidateQueries({ queryKey: ['app', 'data'] });
  };

  const handleThemeToggle = async () => {
    const newMode = themeConfig.mode === 'light' ? 'dark' : 'light';
    await setMode(newMode);
  };

  const handleBookmarkClick = (bookmark: any, workspaceId: string, _sectionId: string) => {
    if (currentWorkspace?.id !== workspaceId) {
      const workspace = appData?.workspaces.find(w => w.id === workspaceId);
      if (workspace) {
        handleWorkspaceChange(workspace);
      }
    }
    const url = normalizeUrl(bookmark.url);
    window.open(url, '_blank');
  };

  // 如果认证或应用数据还在加载中，显示加载状态（超时 8 秒后强制显示）
  const isLoading = (authLoading || isLoadingAppData) && !loadingTimeout;
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">加载中...</p>
          <p className="text-xs text-gray-400 mt-2">若长时间无响应，约 3 秒后自动进入</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            欢迎使用项目导航书签
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            请登录以开始使用
          </p>
          <button
            onClick={() => setShowAuthModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            登录 / 注册
          </button>
        </div>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
          />
        )}
      </div>
    );
  }

  if (!appData) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="relative h-screen h-screen-mobile overflow-hidden">
      {/* 主内容区 */}
      <div className="w-full h-full flex flex-col overflow-hidden" style={{ paddingLeft: `${sidebarWidth}px` }}>
        {/* 顶部工具栏 */}
        <div className="p-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            {isMobile && (
              <button
                onClick={() => setSidebarOverlayOpen(true)}
                className="flex-shrink-0 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] min-w-[44px] touch-manipulation"
                title="工作区"
                aria-label="打开工作区"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <div className="flex-1 min-w-0">
              <SearchBar
                workspaces={appData.workspaces}
                currentWorkspace={currentWorkspace}
                searchMode={searchMode}
                onSearchModeChange={setSearchMode}
                onBookmarkClick={handleBookmarkClick}
              />
            </div>
            <button
              onClick={handleThemeToggle}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors min-h-[44px] min-w-[44px] touch-manipulation"
              title={themeConfig?.mode === 'light' ? '切换到深色模式' : '切换到浅色模式'}
            >
              {themeConfig?.mode === 'light' ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* 工作区布局 */}
        <div className="flex-1 overflow-hidden">
          {currentWorkspace ? (
            <CustomLayout
              workspace={currentWorkspace}
              onDataChange={handleDataRefresh}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              请先添加工作区
            </div>
          )}
        </div>
      </div>

      {/* 左侧 Dock 栏（移动端为覆盖层） */}
      <WorkspaceSidebar
        workspaces={appData.workspaces}
        currentWorkspace={currentWorkspace}
        onWorkspaceChange={handleWorkspaceChange}
        onDataChange={handleDataRefresh}
        onSettingsClick={() => setShowSettings(true)}
        isMobile={isMobile}
        isOverlayOpen={sidebarOverlayOpen}
        onOverlayClose={() => setSidebarOverlayOpen(false)}
      />

      {/* 设置页面 */}
      {showSettings && (
        <Settings
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
};

export default HomePage;
